import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { isSidebarItemVisible } from '@/lib/sidebarVisibility';
import { isPlatformAdmin } from '@/lib/platformAdmin';
import api from '@/lib/api';
import { AuthedImage } from '@/components/shared/AuthedImage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { GlobalSearch } from '@/components/shared/GlobalSearch';
import { useBranding } from '@/lib/branding';
import { useStaffV2Flags } from '@/lib/staffV2Flags';
import { isStaffV2Path, isSharedAdminStaffPath } from './staffV2Paths';
import { DemoModeProvider, DemoModeTrigger as DemoModeTriggerButton } from '@/components/demo/DemoMode';
import { roleLabel } from '@/lib/roleLabels';
import {
  LayoutDashboard, Users, UserCircle, DollarSign, Briefcase,
  Calendar, ClipboardCheck, FileText, LogOut, Menu, Clock,
  MapPin, ChevronRight, ChevronDown, ChevronLeft, Shield, Settings,
  Building2, User, Bell, AlertTriangle, ScrollText, Home, Wrench,
  Banknote, BarChart3, Sparkles, TrendingUp, Lightbulb, Bookmark,
  MessageSquare, Mail, Link2, Globe, Webhook, Search, Plus,
  Heart, Pill, Activity, Zap, Eye,
  PanelLeftClose, PanelLeftOpen, Package,
} from 'lucide-react';
import {
  sidebarConfig,
  sidebarConfigV2,
  staffSidebarConfig,
  QUICK_ACTIONS,
} from '@/config/sidebarConfig';


// ════════════════════════════════════════════════════════════════
// SIDEBAR COMPONENTS
// ════════════════════════════════════════════════════════════════

function CollapsedGroupRail({ group, visibleItems, isGroupActive, location, badges, onClose }) {
  // Collapsed-rail hover-flyout. The trigger (group icon) stays a clickable
  // <Link> that navigates to the first visible item (preserves the pre-flyout
  // behaviour). Hovering for >100ms opens a light flyout panel containing
  // every RBAC/feature-flag-permitted sub-item — clicking any sub-item routes
  // to its existing path. No backend, no permissions, no routing changes.
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const Icon = group.icon;

  const handleEnter = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  };
  const handleLeave = () => {
    // Tiny delay lets the cursor cross the gap between the icon and the panel
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const groupId = group.title.toLowerCase().replace(/[&\s]+/g, '-');
  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      data-testid={`nav-group-${groupId}`}
    >
      <Link
        to={visibleItems[0].path}
        className={`flex items-center justify-center h-10 w-10 mx-auto rounded-lg transition-colors ${isGroupActive ? 'bg-white text-[var(--sidebar-bg,#0B2545)]' : 'text-white/85 hover:bg-white/15 hover:text-white'}`}
        onClick={onClose}
        title={group.title}
      >
        <Icon className="h-5 w-5" />
      </Link>

      {open && (
        <div
          className="absolute left-full top-0 ml-2 z-50 w-56 rounded-lg border border-slate-200 bg-white shadow-xl py-1 animate-in fade-in-0 zoom-in-95 duration-150"
          data-testid={`flyout-${groupId}`}
        >
          <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100">
            {group.title}
          </div>
          <div className="py-1">
            {visibleItems.map(item => {
              const isActive = location.pathname === item.path;
              const badgeCount = item.badge ? badges[item.badge] : 0;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => { setOpen(false); onClose?.(); }}
                  data-testid={`flyout-item-${item.name.toLowerCase().replace(/[\s/]+/g, '-')}`}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm transition-colors ${isActive ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'}`}
                >
                  <span className="flex-1 truncate">{item.name}</span>
                  {badgeCount > 0 && (
                    <Badge variant="destructive" className="h-4 min-w-[18px] text-[10px] px-1.5">
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarGroup({ group, collapsed, hasPermission, location, onClose, badges, userRole, platformAdmin, featureFlags, openGroup, setOpenGroup }) {
  // Accordion-style group: only one group can be expanded at any time.
  // `openGroup` is the currently-open group's title; `setOpenGroup` toggles it.
  const open = openGroup === group.title;
  const Icon = group.icon;

  // Group-level role restriction (e.g., System Admin group)
  if (group.requireRole && userRole !== group.requireRole) return null;

  const visibleItems = group.items.filter(item => isSidebarItemVisible(item, { userRole, featureFlags, hasPermission, platformAdmin }));

  if (visibleItems.length === 0) return null;

  const isGroupActive = visibleItems.some(item => location.pathname === item.path);

  if (collapsed) {
    return <CollapsedGroupRail
      group={group}
      visibleItems={visibleItems}
      isGroupActive={isGroupActive}
      location={location}
      badges={badges}
      onClose={onClose}
    />;
  }

  return (
    <div className="mb-1">
      <button
        onClick={() => setOpenGroup(open ? null : group.title)}
        className={`flex items-center gap-2 w-full px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${isGroupActive ? 'text-white' : 'text-white/80 hover:text-white'}`}
        data-testid={`nav-group-${group.title.toLowerCase().replace(/[&\s]+/g, '-')}`}
      >
        <Icon className="h-4 w-4" />
        <span className="flex-1 text-left">{group.title}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="space-y-0.5 ml-2">
          {visibleItems.map(item => {
            const isActive = location.pathname === item.path;
            const badgeCount = item.badge ? badges[item.badge] : 0;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                data-testid={`nav-${item.name.toLowerCase().replace(/[\s/]+/g, '-')}`}
                className={`flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors ${isActive ? 'bg-white text-[var(--sidebar-bg,#0B2545)] font-medium' : 'text-white/90 hover:bg-white/10 hover:text-white'}`}
              >
                <span className="flex-1">{item.name}</span>
                {badgeCount > 0 && <Badge variant="destructive" className="h-5 min-w-[20px] text-[10px] px-1.5">{badgeCount > 99 ? '99+' : badgeCount}</Badge>}
                {isActive && <ChevronRight className="h-3 w-3 opacity-50" />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SidebarContent({ onClose, collapsed, onToggleCollapse }) {
  const { user, isAdmin, hasPermission, logout } = useAuth();
  const branding = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  // Accordion: track which sidebar group (by title) is currently expanded.
  // null ⇒ all collapsed. Default to first visible group on first render to
  // mimic the previous behaviour where some groups defaulted open, but only
  // ONE may be open at a time.
  const [openGroup, setOpenGroup] = useState(null);
  const [search, setSearch] = useState('');
  const [showQuick, setShowQuick] = useState(false);
  const [badges, setBadges] = useState({});
  const [featureFlags, setFeatureFlags] = useState({});

  const isSystemAdmin = user?.role_name === 'Super Admin';
  const config = isAdmin ? (featureFlags.sidebar_v2_structure_enabled ? sidebarConfigV2 : sidebarConfig) : staffSidebarConfig;

  // Load badge counts + feature flags
  useEffect(() => {
    const loadFlags = async () => {
      try {
        const res = await api.get('/org/settings');
        const flags = res.data?.data?.feature_flags || res.data?.feature_flags || {};
        setFeatureFlags(flags);
      } catch {}
    };
    const loadAdminBadges = async () => {
      try {
        const [alertsRes] = await Promise.all([
          api.get('/org/notifications/my').catch(() => ({ data: { unread_count: 0 } })),
        ]);
        setBadges((prev) => ({ ...prev, alerts: alertsRes.data.unread_count || 0 }));
      } catch {}
    };
    const loadStaffBadges = async () => {
      // SW-scoped pending trip count. Backend scopes /transport/logs to caller
      // when caller lacks transport.view_all (line 390 of routes/transport.py),
      // so this returns ONLY the logged-in worker's own SUBMITTED trips.
      // Master flag OFF → 403, which we silently absorb (badge stays at 0).
      try {
        const r = await api.get('/transport/logs', { params: { status: 'SUBMITTED' } });
        const count = (r.data?.count ?? (r.data?.data?.length || 0));
        setBadges((prev) => ({ ...prev, transport_pending: count }));
      } catch {
        setBadges((prev) => ({ ...prev, transport_pending: 0 }));
      }
    };

    // Flags load for everyone — needed so feature-flagged sidebar items
    // (e.g. "My Trip Logs" with featureFlag: 'transport_logging_enabled') are
    // not silently hidden for non-admin users.
    loadFlags();
    if (isAdmin) {
      loadAdminBadges();
      const interval = setInterval(loadAdminBadges, 60000);
      return () => clearInterval(interval);
    }
    loadStaffBadges();
    const interval = setInterval(loadStaffBadges, 60000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Filter by search
  const filteredConfig = useMemo(() => {
    if (!search.trim()) return config;
    const q = search.toLowerCase();
    return config.map(group => ({
      ...group,
      items: group.items.filter(item => item.name.toLowerCase().includes(q) || group.title.toLowerCase().includes(q)),
    })).filter(group => group.items.length > 0);
  }, [config, search]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Branded sidebar background colour. Uses the org's primary colour from
  // branding context; falls back to the spec'd navy (#0B2545) when missing.
  const sidebarBg = branding.primaryColour || '#0B2545';

  return (
    <div
      className="flex flex-col h-full"
      style={{ backgroundColor: sidebarBg, '--sidebar-bg': sidebarBg }}
    >
      {/* Header */}
      <div className={`flex items-center ${collapsed ? 'flex-col gap-2 p-3' : 'justify-between p-4'}`}>
        {collapsed ? (
          <>
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={branding.organisationName} className="h-8 w-8 rounded-lg object-contain" onError={e => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<div class="h-8 w-8 rounded-lg bg-white/15 flex items-center justify-center"><svg class="h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div>'; }} />
            ) : (
              <div className="h-8 w-8 rounded-lg bg-white/15 flex items-center justify-center">
                <Shield className="h-4 w-4 text-white" />
              </div>
            )}
            {onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                data-testid="expand-sidebar"
                aria-label="Expand sidebar"
                className="group h-8 w-8 rounded-lg bg-white text-[var(--sidebar-bg,#0B2545)] shadow-sm hover:shadow-md flex items-center justify-center transition-all hover:scale-105 ring-1 ring-white/40"
                title="Expand sidebar (show menu labels)"
              >
                <PanelLeftOpen className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2.5">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt={branding.organisationName} className="h-8 w-8 rounded-lg object-contain" onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }} />
              ) : (
                <div className="h-8 w-8 rounded-lg bg-white/15 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-base font-bold tracking-tight text-white">{branding.organisationName}</h1>
                <p className="text-[10px] text-white/70 uppercase tracking-wider">NDIS Provider</p>
              </div>
            </div>
            {onToggleCollapse && (
              <Button variant="ghost" size="icon" className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/10" onClick={onToggleCollapse} data-testid="collapse-sidebar">
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            )}
          </>
        )}
      </div>

      {!collapsed && (
        <>
          {/* Search */}
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/60" />
              <Input
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-8 pl-8 text-xs bg-white/10 border-0 text-white placeholder:text-white/50 focus-visible:ring-white/30"
                data-testid="sidebar-search"
              />
            </div>
          </div>

          {/* Quick Create */}
          {isAdmin && (
            <div className="px-3 pb-2">
              <DropdownMenu open={showQuick} onOpenChange={setShowQuick}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full h-8 text-xs gap-1.5 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white" data-testid="quick-create-btn">
                    <Plus className="h-3.5 w-3.5" />Quick Create
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {QUICK_ACTIONS.map(a => (
                    <DropdownMenuItem key={a.name} onClick={() => { navigate(a.path); setShowQuick(false); onClose?.(); }}>
                      <a.icon className="h-4 w-4 mr-2" />{a.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Demo Mode Trigger — System Admin only */}
          {isSystemAdmin && featureFlags.sales_demo_mode_enabled && (
            <div className="px-3 pb-2">
              <DemoModeTriggerButton />
            </div>
          )}
        </>
      )}

      <Separator className="bg-white/15" />

      {/* Nav Groups */}
      <nav className="flex-1 overflow-y-auto py-2 px-1.5" data-testid="sidebar-nav">
        {filteredConfig.map(group => (
          <SidebarGroup
            key={group.title}
            group={group}
            collapsed={collapsed}
            hasPermission={hasPermission}
            location={location}
            onClose={onClose}
            badges={badges}
            userRole={user?.role_name}
            platformAdmin={isPlatformAdmin(user)}
            featureFlags={featureFlags}
            openGroup={openGroup}
            setOpenGroup={setOpenGroup}
          />
        ))}
      </nav>

      <Separator className="bg-white/15" />

      {/* User */}
      <div className={collapsed ? 'p-2' : 'p-3'}>
        {collapsed ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={handleLogout} className="h-9 w-9 mx-auto rounded-full bg-white/15 flex items-center justify-center text-sm font-medium text-white hover:bg-destructive/30 hover:text-white transition-colors overflow-hidden">
                  {user?.profile_photo_url ? <AuthedImage src={user.profile_photo_url} alt="" className="h-full w-full rounded-full object-cover" fallback={<>{user?.full_name?.charAt(0) || '?'}</>} /> : (user?.full_name?.charAt(0) || '?')}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{user?.full_name} — Sign out</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <>
            <div className="flex items-center gap-2.5 mb-2">
              <div className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center text-sm font-medium text-white shrink-0 overflow-hidden">
                {user?.profile_photo_url ? <AuthedImage src={user.profile_photo_url} alt="" className="h-full w-full rounded-full object-cover" fallback={<>{user?.full_name?.charAt(0) || '?'}</>} /> : (user?.full_name?.charAt(0) || '?')}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate text-white">{user?.full_name}</p>
                <p className="text-[10px] text-white/70 truncate">{roleLabel(user?.role_name)}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="w-full justify-start text-xs text-white/80 hover:text-white hover:bg-white/10 h-7" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="h-3.5 w-3.5 mr-2" />Sign out
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// APP SHELL
// ════════════════════════════════════════════════════════════════

export function AppShell({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const shellLocation = useLocation();
  const staffV2Flags = useStaffV2Flags();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop sidebar collapsed-by-default on login. Persist user preference
  // in localStorage so toggling sticks across navigations and reloads, but
  // initialise to `true` (collapsed) the first time we see this browser.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem('gc_sidebar_collapsed');
      if (raw === null) return true; // first visit ⇒ collapsed
      return raw === '1';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try { localStorage.setItem('gc_sidebar_collapsed', collapsed ? '1' : '0'); } catch {}
  }, [collapsed]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await api.get('/org/branches');
        setBranches(res.data.data || []);
        const assigned = user?.assigned_branch_ids || [];
        if (assigned.length === 1) setSelectedBranch(assigned[0]);
      } catch (e) {}
    };
    if (user) fetchBranches();
  }, [user]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await api.get('/org/notifications/my');
        setNotifications(res.data.data || []);
        setUnreadCount(res.data.unread_count || 0);
      } catch (e) {}
    };
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const userBranches = user?.assigned_branch_ids?.length > 0
    ? branches.filter(b => user.assigned_branch_ids.includes(b.code))
    : branches;
  const showBranchSelector = userBranches.length > 1 || (user?.scope_mode === 'ALL' && branches.length > 1);

  // Staff Portal v2 bypass — those routes render their own StaffPortalShell.
  // Exception: on shared admin/staff routes (e.g. Incidents, Notifications,
  // Staff Policies) elevated/management roles keep the full admin chrome — only
  // Support Workers get the chrome-less pass-through to StaffPortalShell.
  const staffPortalTreatment = staffV2Flags.staff_portal_v2_enabled
    && isStaffV2Path(shellLocation.pathname)
    && !(isAdmin && isSharedAdminStaffPath(shellLocation.pathname));
  if (staffPortalTreatment) {
    return <DemoModeProvider>{children}</DemoModeProvider>;
  }

  return (
    <DemoModeProvider>
    <div className="flex h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex lg:flex-col border-r transition-all duration-200 ${collapsed ? 'lg:w-[60px]' : 'lg:w-[260px]'}`}>
        <SidebarContent onClose={() => {}} collapsed={collapsed} onToggleCollapse={() => setCollapsed(!collapsed)} />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[280px] p-0">
          <SidebarContent onClose={() => setMobileOpen(false)} collapsed={false} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 sticky top-0 z-40 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b flex items-center px-4 lg:px-6 gap-3">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button" aria-label="Open navigation menu">
            <Menu className="h-5 w-5" />
          </Button>

          {/* Expand button when collapsed (secondary affordance — the primary
              expand pill lives on the sidebar rail itself). Larger + bordered
              for visibility against light headers. */}
          {collapsed && (
            <Button
              variant="outline"
              size="sm"
              className="hidden lg:inline-flex h-9 px-3 gap-1.5 border-slate-300 bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 shadow-sm"
              onClick={() => setCollapsed(false)}
              data-testid="expand-sidebar-secondary"
              title="Expand sidebar"
            >
              <PanelLeftOpen className="h-4 w-4" />
              <span className="text-xs font-medium">Menu</span>
            </Button>
          )}

          {showBranchSelector && (
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="branch-selector">
                  <SelectValue placeholder="All branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {(user?.scope_mode === 'ALL' ? branches : userBranches).map(b => (
                    <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex-1" />

          {/* Global Search */}
          <GlobalSearch />

          {/* Notification Bell */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell" aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}>
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground flex items-center justify-center px-1">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>Notifications</span>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={async () => {
                    try { await api.post('/org/notifications/read-all'); setUnreadCount(0); setNotifications(prev => prev.map(n => ({...n, is_read: true}))); } catch {}
                  }} data-testid="mark-all-read">Mark all read</Button>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {notifications.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">No notifications</div>
              ) : notifications.slice(0, 10).map(n => (
                <DropdownMenuItem key={n.id} className={`flex flex-col items-start gap-1 py-2 ${!n.is_read ? 'bg-accent/30' : ''}`}>
                  <p className={`text-sm ${!n.is_read ? 'font-medium' : ''}`}>{n.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                  <p className="text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleString()}</p>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 h-9 px-3" data-testid="user-menu-trigger">
                <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary overflow-hidden">
                  {user?.profile_photo_url ? <AuthedImage src={user.profile_photo_url} alt="" className="h-full w-full rounded-full object-cover" fallback={<>{user?.full_name?.charAt(0) || '?'}</>} /> : (user?.full_name?.charAt(0) || '?')}
                </div>
                <span className="hidden md:inline text-sm font-medium">{user?.full_name}</span>
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <p className="text-sm font-medium">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{roleLabel(user?.role_name)} · {user?.scope_mode}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="user-menu-logout">
                <LogOut className="h-4 w-4 mr-2" />Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto min-w-0">
          <div className="w-full max-w-none px-4 md:px-6 lg:px-8 py-6">{children}</div>
        </main>
      </div>
    </div>
    </DemoModeProvider>
  );
}
